import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressCard } from "@fortawesome/free-solid-svg-icons";

import { getRuta, simpleMessage } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import { getToken } from "../../../api/Account";
import { DataContext } from "../../../context/DataContext";
import { addNominaAsync, getNominaAsync } from "../../../api/Employees";
import { formatearFecha } from "../../../helpers/formato";
import {
  CargandoTabla,
  EstadoChip,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import {
  ERROR_OTRA_SESION,
  celdaFecha,
  encabezadoTabla,
  estadoDeNomina,
  nominaAbierta,
  nominaCerrada,
  normalizar,
} from "./comunes";

const FILAS_POR_PAGINA = 10;

const NominaList = () => {
  const [nominaList, setNominaList] = useState([]);
  const [localReload, setLocalReload] = useState(false);

  // Estado local de la lista: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const peticionActual = useRef(0);

  // "Iniciar nómina": el overlay global no bloquea el teclado, así que el ref corta el segundo
  // envío al instante y el estado deshabilita el botón mientras la petición sigue en curso.
  const enviando = useRef(false);
  const [iniciando, setIniciando] = useState(false);

  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
  const aviso = useAviso();

  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [pagina, setPagina] = useState(0);

  const termino = normalizar(searchTerm.trim());
  const withSearch = nominaList.filter((val) => {
    if (termino === "") return true;
    return (
      normalizar(val.id).includes(termino) || normalizar(val.description).includes(termino)
    );
  });

  const getNominaList = async () => {
    const peticion = ++peticionActual.current;
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await getNominaAsync(token);
      if (peticion !== peticionActual.current) return;

      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        setError(true);
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return;
      }

      setNominaList(result.data.result);
      setIsLoading(false);
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      if (peticion !== peticionActual.current) return;
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  };

  useEffect(() => {
    getNominaList();
  }, [localReload]);

  // Si al buscar la página actual queda fuera de rango, se vuelve a la primera.
  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= withSearch.length) {
      setPagina(0);
    }
  }, [pagina, withSearch.length]);

  const currentItem = withSearch.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  const onChangeSearch = (val) => {
    setPagina(0);
    setSearchTerm(val);
  };

  const openNomina = async () => {
    if (enviando.current) return;
    enviando.current = true;
    setIniciando(true);
    setIsLoading(true);
    try {
      const result = await addNominaAsync(token);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
          return;
        }
        aviso.error(result.error || "No se pudo iniciar la nómina");
        return;
      }
      // B1 (auditoría): esta línea no vuelve a cargar la lista porque no cambia el valor.
      // Se deja igual a propósito: arreglarlo cambia el comportamiento y va como propuesta aparte.
      setLocalReload(localReload);
      setIsLoading(false);
      aviso.exito("Nómina iniciada");
    } catch (e) {
      setIsLoading(false);
      aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    } finally {
      enviando.current = false;
      setIniciando(false);
    }
  };

  // Mismo permiso que antes. Mientras la lista carga o falla no se sabe si ya hay una nómina,
  // así que el botón espera a saberlo.
  // Cada acción pide el permiso que exige su endpoint (EmployeesController): iniciar es AddNomina
  // (NOMINA CREAR) y ver la nómina es GetNominaById (NOMINA VER), no UPDATE.
  const puedeIniciar =
    tienePermiso(access, "NOMINA CREAR") && !cargando && !error && nominaList.length === 0;
  const puedeAbrir = tienePermiso(access, "NOMINA VER");

  const abierta = nominaList.find(nominaAbierta);

  const botonIniciar = (
    <Button
      onClick={() => openNomina()}
      startIcon={<AddIcon />}
      variant="contained"
      disabled={iniciando}
    >
      Iniciar nómina
    </Button>
  );

  const abrirNomina = (id) => navigate(`${ruta}/nominadetails/${id}`);

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={8} columnas={6} etiqueta="Cargando nóminas…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar las nóminas"
        }
        onReintentar={getNominaList}
      />
    );
  } else if (withSearch.length === 0) {
    contenido =
      termino !== "" ? (
        <EstadoVacio
          titulo="Sin resultados"
          descripcion={`Ninguna nómina coincide con «${searchTerm.trim()}». Revisa el número o el nombre del período.`}
          accion={
            <Button variant="outlined" onClick={() => onChangeSearch("")}>
              Borrar búsqueda
            </Button>
          }
        />
      ) : (
        <EstadoVacio
          titulo="Todavía no hay nóminas"
          descripcion="Aquí aparece el historial de pagos quincenales. Inicia la primera nómina; las siguientes se crean solas al cerrar cada una."
          accion={puedeIniciar ? botonIniciar : undefined}
        />
      );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table
            aria-label="Lista de nóminas"
            sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  #
                </TableCell>
                <TableCell sx={encabezadoTabla}>Período</TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                >
                  Desde
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                >
                  Hasta
                </TableCell>
                <TableCell sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}>
                  Estado
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  Fecha de pago
                </TableCell>
                {puedeAbrir ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Acciones
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>

            <TableBody>
              {currentItem.map((row) => {
                const { description, desde, estado, fechaPago, hasta, id } = row;
                const esAbierta = nominaAbierta(row);

                return (
                  <TableRow
                    key={id}
                    hover
                    sx={
                      esAbierta
                        ? { backgroundColor: (theme) => theme.palette.soft?.primary }
                        : undefined
                    }
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      {id}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {description}
                      </Typography>
                      {/* En pantallas pequeñas las fechas pasan debajo del nombre del período. */}
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: { xs: "block", md: "none" },
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {`${formatearFecha(desde)} – ${formatearFecha(hasta)}`}
                      </Typography>
                      {/* La columna "Fecha de pago" no cabe en el teléfono: pasa a esta línea. */}
                      {nominaCerrada(row) ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: { xs: "block", sm: "none" },
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {`Fecha de pago: ${formatearFecha(fechaPago)}`}
                        </Typography>
                      ) : null}
                      {/* En el teléfono el estado pasa debajo de las fechas. */}
                      <Box sx={{ display: { xs: "block", sm: "none" }, mt: 0.5 }}>
                        <EstadoChip {...estadoDeNomina(estado)} />
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ ...celdaFecha, display: { xs: "none", md: "table-cell" } }}
                    >
                      {formatearFecha(desde)}
                    </TableCell>
                    <TableCell
                      sx={{ ...celdaFecha, display: { xs: "none", md: "table-cell" } }}
                    >
                      {formatearFecha(hasta)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      <EstadoChip {...estadoDeNomina(estado)} />
                    </TableCell>
                    <TableCell
                      sx={{ ...celdaFecha, display: { xs: "none", sm: "table-cell" } }}
                    >
                      {nominaCerrada(row) ? formatearFecha(fechaPago) : "—"}
                    </TableCell>
                    {puedeAbrir ? (
                      <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <Tooltip title="Ver nómina">
                          <IconButton
                            aria-label={`Ver nómina: ${description}`}
                            onClick={() => abrirNomina(id)}
                            sx={{ color: "primary.main" }}
                          >
                            <VisibilityOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={withSearch.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(tipo) =>
            ({
              first: "Primera página",
              last: "Última página",
              next: "Página siguiente",
              previous: "Página anterior",
            }[tipo])
          }
        />
      </>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Nóminas"
        subtitulo="Historial de pagos quincenales del personal"
        icono={<FontAwesomeIcon icon={faAddressCard} />}
        acciones={puedeIniciar ? botonIniciar : undefined}
      />

      {!cargando && !error && abierta ? (
        <Paper
          component="section"
          aria-label="Nómina abierta"
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            backgroundColor: (theme) => theme.palette.soft?.primary,
            borderColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}
              >
                Nómina abierta
              </Typography>
              <Typography
                component="h2"
                variant="h6"
                sx={{ fontWeight: 700, color: "primary.main", overflowWrap: "anywhere" }}
              >
                {abierta.description}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}
              >
                {`Del ${formatearFecha(abierta.desde)} al ${formatearFecha(abierta.hasta)}`}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                La nómina siguiente se crea sola al cerrar esta.
              </Typography>
            </Box>
            {puedeAbrir ? (
              <Button variant="contained" onClick={() => abrirNomina(abierta.id)}>
                Abrir nómina
              </Button>
            ) : null}
          </Box>
        </Paper>
      ) : null}

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <TextField
          size="small"
          type="search"
          onChange={(e) => onChangeSearch(e.target.value)}
          value={searchTerm}
          placeholder="Número o nombre del período"
          inputProps={{ "aria-label": "Buscar nómina", autoComplete: "off" }}
          sx={{ width: { xs: "100%", md: 380 }, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Borrar búsqueda"
                  onClick={() => onChangeSearch("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {contenido}
      </Paper>
    </Container>
  );
};

export default NominaList;
